import North from "@mui/icons-material/North";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { ObjectId } from "../../interfaces/common";

interface ChatInputProps {
  chatId: ObjectId;
  loading: boolean;
  sendMessage: Function;
  messageProposition?: string[];
  multiline?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  chatId,
  loading,
  sendMessage,
  messageProposition,
  multiline = true,
}) => {
  const [userInput, setUserInput] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    setUserInput("");
  }, [chatId]);

  useEffect(() => {
    if (textareaRef.current) {
      const textareaElement = textareaRef.current;
      textareaElement.style.height = "auto";
      textareaElement.style.height = `${textareaElement.scrollHeight}px`;
    }
  }, [userInput]);

  const handleSendMessage = async (userInput: string) => {
    setUserInput("");
    await sendMessage(userInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (!e.shiftKey) {
        e.preventDefault();
        if (userInput) {
          handleSendMessage(userInput);
        }
      } else {
        e.preventDefault();
        const textarea = e.target as HTMLTextAreaElement;
        const { selectionStart, selectionEnd } = textarea;

        setUserInput((prevInput) => {
          return (
            prevInput.slice(0, selectionStart) +
            "\n" +
            prevInput.slice(selectionEnd)
          );
        });

        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = selectionStart + 1;
        }, 0);
      }
    }
  };

  return (
    <Stack spacing={2}>
      {messageProposition && (
        <Stack spacing={0.5} direction="row">
          {messageProposition.map((message) => (
            <Paper
              className="flex-col p-2 text-center rounded-xl border-solid border-slate-200 shadow-[0_9px_9px_0px_rgba(0,0,0,0.06),_0_2px_5px_0px_rgba(0,0,0,0.06)] hover:bg-slate-50 cursor-pointer"
              key={message}
              onClick={() => setUserInput(message)}
            >
              {message}
            </Paper>
          ))}
        </Stack>
      )}
      {multiline ? (
        <Paper className="flex flex-col rounded-xl border-solid border-slate-200 shadow-[0_9px_9px_0px_rgba(0,0,0,0.06),_0_2px_5px_0px_rgba(0,0,0,0.06)]">
          <InputBase
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Envoyer un message..."
            inputProps={{ "aria-label": "Envoyer un message" }}
            onKeyDown={handleKeyDown}
            className="flex-1 pt-4 px-4"
            multiline={multiline}
            minRows={2}
            fullWidth
          />
          <div className="flex items-center justify-end p-2">
            <IconButton
              onClick={() => handleSendMessage(userInput)}
              disabled={!userInput || loading}
              className={classNames({
                "bg-[--mui-palette-primary-main]": !!userInput,
                invisible: !userInput,
              })}
              size="small"
            >
              <North className="text-white" />
            </IconButton>
          </div>
        </Paper>
      ) : (
        <Paper className="flex rounded-xl border-solid border-slate-200 shadow-[0_9px_9px_0px_rgba(0,0,0,0.06),_0_2px_5px_0px_rgba(0,0,0,0.06)]">
          <InputBase
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Envoyer un message..."
            inputProps={{ "aria-label": "Envoyer un message" }}
            onKeyDown={handleKeyDown}
            className="flex-1 py-2 pl-3 overflow-scroll"
            fullWidth
            multiline
            maxRows={4}
          />
          <div className="flex flex-col items-center justify-end pl-2 pr-3 py-2">
            <IconButton
              onClick={() => handleSendMessage(userInput)}
              disabled={!userInput || loading}
              className={classNames({
                "bg-[--mui-palette-primary-main]": !!userInput,
                invisible: !userInput,
              })}
              size="small"
            >
              <North className="text-white" fontSize="small" />
            </IconButton>
          </div>
        </Paper>
      )}
    </Stack>
  );
};

export default ChatInput;
